<template>
  <div class="flex h-full flex-col">
    <header class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="text-lg font-bold">Tệp tin trên máy chủ</h1>
        <p class="text-xs opacity-60">
          Các tệp được agent tải lên qua shell (bucket: shell)
        </p>
      </div>
      <div class="flex items-center gap-2">
        <input
          v-model.trim="query"
          type="search"
          class="input input-sm input-bordered"
          placeholder="Tìm tên tệp..."
          @keyup.enter="applyFilter"
        />
        <button class="btn btn-sm" @click="refresh" :disabled="loading">
          <i class="fa-solid fa-refresh" v-if="!loading"></i>
          <i class="fa-solid fa-spin fa-refresh" v-else></i>
          Làm mới
        </button>
      </div>
    </header>

    <div
      class="flex-1 overflow-hidden rounded-box bg-base-100 p-4 text-sm shadow-sm"
    >
      <table class="table">
        <thead>
          <tr>
            <th>Tên tệp</th>
            <th>Kích cỡ</th>
            <th>Thời gian</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in visibleFiles" :key="file.name">
            <td class="max-w-sm break-all">
              <div class="flex items-baseline gap-1">
                <i class="fa-solid fa-file-text text-primary"></i>
                {{ file.name }}
              </div>
            </td>
            <td>{{ prettyBytes(file.size ?? 0) }}</td>
            <td>{{ formatDate(file.last_modified) }}</td>
            <td class="text-right">
              <button
                class="btn btn-ghost btn-sm gap-1"
                @click="download(file.name)"
                :disabled="downloading === file.name"
                title="Tải xuống"
              >
                <i class="fa-solid fa-download"></i>
                <template v-if="downloading === file.name">Đang tải...</template>
                <template v-else>Tài xuống</template>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="visibleFiles.length === 0 && !loading"
        class="py-12 text-center opacity-60"
      >
        Không có tệp nào.
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12">
        <span class="loading loading-spinner loading-xl"></span>
      </div>

      <div v-if="canLoadMore && !loading" class="pt-4 text-center">
        <button class="btn btn-link" @click="loadMore">Tải thêm</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import dayjs from "dayjs";
import prettyBytes from "pretty-bytes";
import { toast } from "vue3-toastify";
import { downloadShellFile, listShellFiles, type ShellFile } from "@/api/files";

const files = ref<ShellFile[]>([]);
const marker = ref("");
const hasMore = ref(true);
const query = ref("");
const loading = ref(false);
const downloading = ref<string>("");

const visibleFiles = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return files.value;
  return files.value.filter((f) => f.name.toLowerCase().includes(q));
});

const canLoadMore = computed(() => hasMore.value && !loading.value);

async function loadInitial() {
  loading.value = true;
  try {
    const res = await listShellFiles(marker.value);
    const incoming = res.files ?? [];
    if (marker.value === "") {
      files.value = incoming;
    } else {
      files.value = [...files.value, ...incoming];
    }
    marker.value = res.next_marker ?? "";
    hasMore.value = !!marker.value;
  } catch (err) {
    toast.error(err instanceof Error ? err.message : String(err));
    files.value = [];
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  await loadInitial();
}

function applyFilter() {
  // Client-side filter re-renders via visibleFiles computed.
}

function refresh() {
  files.value = [];
  marker.value = "";
  hasMore.value = true;
  query.value = "";
  loadInitial();
}

function download(name: string) {
  downloading.value = name;
  try {
    downloadShellFile(name);
  } catch (err) {
    toast.error(err instanceof Error ? err.message : String(err));
  } finally {
    setTimeout(() => {
      if (downloading.value === name) downloading.value = "";
    }, 3000);
  }
}

function formatDate(v: string) {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY/MM/DD HH:mm") : v;
}

onMounted(loadInitial);
</script>
